import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Button asChild variant="outline" className="mb-6">
          <a href="/">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </a>
        </Button>

        <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
        <p className="text-gray-600 mb-8">Last Updated: December 2025</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              By accessing and using the website comparebeforeyoubuy.shop and purchasing products from Compare Before You Buy, you agree to be bound by these Terms and Conditions. If you do not agree, please do not use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Company Information</h2>
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <p className="text-gray-900 font-semibold mb-2">Compare Before You Buy</p>
              <p className="text-gray-700">91 CC/A Phase 4 DHA Lahore, Lahore, 54000, Pakistan</p>
              <p className="text-gray-700 mt-2">Email: contact@comparebeforeyoubuy.shop</p>
              <p className="text-gray-700">Phone: 08006521866 (UK Customer Support)</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Nature of Service – Informational Use Disclaimer</h2>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">⚠️ Important Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                CompareBeforeYouBuy is an <strong>informational and comparison website</strong>. We provide product information, specifications, and pricing comparisons to help consumers make informed purchasing decisions. 
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>We do not guarantee:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>The accuracy, completeness, or timeliness of product information</li>
                <li>Current availability of products or pricing</li>
                <li>That products will meet your specific requirements</li>
                <li>That product descriptions are error-free</li>
              </ul>
            </div>

            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Your Responsibility:</strong> You should verify all product information, specifications, pricing, and availability directly with retailers or manufacturers before making a purchase decision. Product specifications, prices, and availability are subject to change without notice.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              While we make every effort to ensure accuracy, we are not responsible for errors, omissions, or changes in product information provided by manufacturers or retailers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3A. Affiliate Marketing Disclosure</h2>
            
            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">💰 Affiliate Relationships</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                CompareBeforeYouBuy participates in affiliate advertising programs. This means:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Some product links on our website are affiliate links</li>
                <li>We may earn a commission when you click these links and make purchases</li>
                <li>Commissions are paid by retailers, not by you</li>
                <li>You pay the same price whether you use our links or not</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">How Affiliate Links Work</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you click a product link on our site and complete a purchase from a partner retailer, that retailer may pay us a small commission. This is a standard industry practice that allows us to provide free comparison services.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Editorial Independence</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our affiliate partnerships do not:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Determine which products we feature or recommend</li>
              <li>Influence product rankings or order of presentation</li>
              <li>Affect the objectivity or accuracy of product comparisons</li>
              <li>Result in biased reviews or recommendations</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              See our <a href="/editorial-policy" className="text-blue-600 hover:underline">Editorial Policy</a> for full transparency about our research methodology and editorial standards.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3B. Products and Services</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We provide information about SIM-free, unlocked smartphones and mobile technology. Product information is sourced from manufacturers and retailers and is subject to change.
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Product availability and pricing may vary</li>
              <li>Specifications are based on manufacturer data</li>
              <li>Actual products may differ from descriptions</li>
              <li>We are not responsible for retailer inventory or fulfillment</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Pricing and Payment</h2>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>All prices are in British Pounds (GBP) and include VAT where applicable</li>
              <li>Prices may change without notice</li>
              <li>Payment is due at the time of order</li>
              <li>We accept major credit/debit cards and other payment methods as displayed</li>
              <li>All transactions are processed securely</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Orders and Delivery</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Order Confirmation</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Once you place an order, you will receive an email confirmation. This does not constitute acceptance of your order. We reserve the right to refuse or cancel any order.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Delivery</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Delivery is available to UK addresses</li>
              <li>Delivery times are estimates and not guaranteed</li>
              <li>We are not liable for delays caused by courier services</li>
              <li>You must provide accurate delivery information</li>
              <li>Signature may be required upon delivery</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Consumer Rights (UK)</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Under the Consumer Rights Act 2015, you have the right to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Cancel your order within 14 days of receipt (see Refund Policy)</li>
              <li>Receive goods that match their description</li>
              <li>Receive goods of satisfactory quality</li>
              <li>Receive goods fit for purpose</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Warranty and Returns</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              All products come with the manufacturer's standard warranty. Warranty periods vary by manufacturer (typically 12 months). For warranty claims, please contact us first before approaching the manufacturer.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              See our separate Refund Policy for detailed return information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
            
            <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">⚠️ Liability Limitations</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the fullest extent permitted by law, CompareBeforeYouBuy and its directors, employees, partners, and affiliates shall not be liable for:
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Exclusions and Limitations</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>Indirect or Consequential Damages:</strong> Including but not limited to loss of profits, revenue, data, business opportunities, or goodwill</li>
              <li><strong>Information Accuracy:</strong> Any errors, omissions, or inaccuracies in product information, pricing, or specifications</li>
              <li><strong>Third-Party Actions:</strong> Actions, omissions, or conduct of retailers, manufacturers, or other third parties</li>
              <li><strong>Service Interruptions:</strong> Downtime, technical issues, or unavailability of our website</li>
              <li><strong>External Links:</strong> Content, products, or services available through third-party websites</li>
              <li><strong>User Decisions:</strong> Purchasing decisions made based on information provided on our website</li>
              <li><strong>Financial Loss:</strong> Any financial loss resulting from following recommendations or comparisons</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Maximum Liability Cap</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              In any event, our total aggregate liability to you for all claims arising from or related to the use of our website shall not exceed £100 (one hundred pounds sterling).
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Exceptions</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Nothing in these Terms and Conditions excludes or limits our liability for:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Death or personal injury caused by our negligence</li>
              <li>Fraud or fraudulent misrepresentation</li>
              <li>Any liability that cannot be excluded or limited under applicable law</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Your Responsibility</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You acknowledge that:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>You use our website at your own risk</li>
              <li>You are responsible for verifying information before making purchase decisions</li>
              <li>Transactions are between you and the retailer, not CompareBeforeYouBuy</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Intellectual Property Rights</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Website Content Ownership</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              All content on comparebeforeyoubuy.shop, including but not limited to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Text, articles, and written content</li>
              <li>Graphics, logos, icons, and images</li>
              <li>Website design, layout, and user interface</li>
              <li>Software, code, and functionality</li>
              <li>Compilations and databases</li>
              <li>Trademarks and service marks</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              ...is the exclusive property of CompareBeforeYouBuy or its licensors and is protected by UK and international copyright, trademark, and intellectual property laws.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Permitted Use</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>View and browse website content for personal, non-commercial use</li>
              <li>Print single copies of pages for personal reference</li>
              <li>Share links to our website on social media</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Prohibited Use</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Without our prior written consent, you may NOT:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Copy, reproduce, republish, or redistribute our content</li>
              <li>Modify, adapt, or create derivative works</li>
              <li>Use our content for commercial purposes</li>
              <li>Frame or embed our website on another site</li>
              <li>Scrape, mine, or automatically extract data</li>
              <li>Use our trademarks, logos, or branding</li>
              <li>Remove copyright notices or watermarks</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Third-Party Intellectual Property</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Product names, logos, brands, and trademarks displayed on our website are the property of their respective owners. Reference to these brands does not imply endorsement or affiliation unless explicitly stated.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Copyright Infringement</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you believe your intellectual property rights have been infringed, contact us at <a href="mailto:contact@comparebeforeyoubuy.shop" className="text-blue-600 hover:underline">contact@comparebeforeyoubuy.shop</a> with:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Description of the copyrighted work</li>
              <li>Location of the infringing material on our site</li>
              <li>Your contact information</li>
              <li>A statement of good faith belief that use is not authorized</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9A. External Links Disclaimer</h2>
            
            <div className="bg-purple-50 border-l-4 border-purple-600 p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">🔗 Third-Party Websites</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our website contains links to external websites operated by third parties, including retailers, manufacturers, and affiliate partners. <strong>We are not responsible for the content, practices, or policies of these external sites.</strong>
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">What We Are NOT Responsible For</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you click on external links, you leave our website. We have no control over and assume no responsibility for:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><strong>Content Accuracy:</strong> The accuracy, legality, or appropriateness of content on external websites</li>
              <li><strong>Privacy Practices:</strong> How external sites collect, use, or protect your personal information</li>
              <li><strong>Terms of Service:</strong> The terms, conditions, or policies of third-party websites</li>
              <li><strong>Transactions:</strong> Products, services, or transactions conducted on external websites</li>
              <li><strong>Security:</strong> The security measures or data protection of third-party sites</li>
              <li><strong>Availability:</strong> Whether external links remain active or accessible</li>
              <li><strong>Quality of Goods:</strong> The quality, safety, or legality of products sold by retailers</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Your Responsibility</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When using external links:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Review the privacy policy and terms of service of each website you visit</li>
              <li>Understand that different rules and policies may apply</li>
              <li>Verify retailer legitimacy and security before providing payment information</li>
              <li>Exercise caution and use your own judgment</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Affiliate Links</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Some external links are affiliate links. While we may earn commissions from purchases, this does not:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Make us responsible for retailer conduct or fulfillment</li>
              <li>Create any warranty or guarantee about products or services</li>
              <li>Establish a partnership or agency relationship with retailers</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Links to Our Website</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may link to our homepage provided you do so in a fair and legal manner that does not damage our reputation or take advantage of it. You must not establish a link in a way that suggests any form of association, approval, or endorsement without our consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Privacy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your use of our website is also governed by our Privacy Policy. Please review it to understand how we collect and use your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law and Jurisdiction</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Applicable Law</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms and Conditions, and any dispute or claim arising out of or in connection with them or their subject matter or formation (including non-contractual disputes or claims), shall be governed by and construed in accordance with the laws of <strong>England and Wales</strong>.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Jurisdiction</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Both parties irrevocably agree that the courts of <strong>England and Wales</strong> shall have exclusive jurisdiction to settle any dispute or claim arising out of or in connection with these Terms and Conditions or their subject matter or formation (including non-contractual disputes or claims).
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Consumer Rights</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you are a consumer resident in the UK, you will benefit from any mandatory provisions of the law of the country in which you are resident. Nothing in these Terms and Conditions affects your rights as a consumer to rely on such mandatory provisions of local law.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Dispute Resolution</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Before initiating legal proceedings, we encourage you to contact us to resolve any disputes informally:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Email: <a href="mailto:contact@comparebeforeyoubuy.shop" className="text-blue-600 hover:underline">contact@comparebeforeyoubuy.shop</a></li>
              <li>Phone: <a href="tel:08006521866" className="text-blue-600 hover:underline">08006521866</a> (UK Customer Support)</li>
              <li>Post: 91 CC/A Phase 4 DHA Lahore, Lahore, 54000, Pakistan</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Alternative Dispute Resolution</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you are a consumer, you may also have the right to refer disputes to an alternative dispute resolution body. We are not obliged to participate in alternative dispute resolution procedures but may agree to do so on a case-by-case basis.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting. Continued use of our services constitutes acceptance of modified terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For questions about these Terms and Conditions:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700">
                Email: <a href="mailto:contact@comparebeforeyoubuy.shop" className="text-blue-600 hover:underline">contact@comparebeforeyoubuy.shop</a>
              </p>
              <p className="text-gray-700">
                Phone: <a href="tel:08006521866" className="text-blue-600 hover:underline">08006521866</a>
              </p>
              <p className="text-gray-700 mt-2">
                Operating Hours: Monday-Friday 9am-6pm, Saturday 10am-4pm
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
